import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import userService from '../../services/userServices';
import styles from './ProjectsSettings.module.css';

export default function ProjectsSettings() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [shares, setShares] = useState({ users: [], teams: [] });
  const [sharesLoading, setSharesLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await userService.getAccessibleProjects();
      if (response && response.data) {
        setProjects(response.data);
        if (response.data.length > 0) {
          setSelectedProject(response.data[0]);
          loadProjectShares(response.data[0].project.id);
        }
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectShares = async (projectId) => {
    try {
      setSharesLoading(true);
      const response = await userService.getProjectShares(projectId);
      if (response && response.data) {
        setShares(response.data);
      }
    } catch (error) {
      console.error('Failed to load project shares:', error);
    } finally {
      setSharesLoading(false);
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    loadProjectShares(project.project.id);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedProjects = () => {
    if (!projects.length) return [];
    
    return [...projects].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'name':
          aValue = a.project.name.toLowerCase();
          bValue = b.project.name.toLowerCase();
          break;
        case 'status':
          aValue = a.project.status || 'Active';
          bValue = b.project.status || 'Active';
          break;
        case 'created':
          aValue = new Date(a.project.created_at || a.project.created_date);
          bValue = new Date(b.project.created_at || b.project.created_date);
          break;
        case 'access':
          aValue = getSharedViaText(a).toLowerCase();
          bValue = getSharedViaText(b).toLowerCase();
          break;
        case 'role':
          aValue = a.access_level.toLowerCase();
          bValue = b.access_level.toLowerCase();
          break;
        default:
          aValue = a.project.name.toLowerCase();
          bValue = b.project.name.toLowerCase();
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getAccessLevelColor = (accessLevel) => {
    switch (accessLevel) {
      case 'owner': return 'var(--primary)';
      case 'admin': return 'var(--warning)';
      case 'write': return 'var(--success)';
      case 'read': return 'var(--muted-foreground)';
      default: return 'var(--muted-foreground)';
    }
  };

  const getSharedViaText = (project) => {
    if (project.access_level === 'owner') {
      return 'Owned by you';
    } else if (project.shared_via === 'user') {
      return 'Shared with you directly';
    } else if (project.shared_via === 'team') {
      return `Shared via team: ${project.team_name || 'Unknown Team'}`;
    }
    return 'Unknown access method';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading projects...</p>
      </div>
    );
  }

  return (
    <div className={styles.projectsSettings}>
      <div className={styles.header}>
        <div>
          <h1>Project Management</h1>
          <p className={styles.subtitle}>Manage your projects and sharing permissions</p>
        </div>
      </div>

      <div className={styles.projectsContainer}>
        {/* Left Column - Projects List */}
        <div className={styles.projectsList}>
          <div className={styles.sectionHeader}>
            <h2>Your Projects</h2>
            <span className={styles.projectCount}>{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
          </div>
          
          <div className={styles.projectsTable}>
            <div className={styles.tableHeader}>
              <div 
                className={`${styles.tableCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('name')}
              >
                Name
                <div className={styles.sortIcon}>
                  {sortConfig.key === 'name' ? (
                    sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  ) : (
                    <div className={styles.sortIconPlaceholder} />
                  )}
                </div>
              </div>
              <div 
                className={`${styles.tableCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('status')}
              >
                Status
                <div className={styles.sortIcon}>
                  {sortConfig.key === 'status' ? (
                    sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  ) : (
                    <div className={styles.sortIconPlaceholder} />
                  )}
                </div>
              </div>
              <div 
                className={`${styles.tableCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('created')}
              >
                Created
                <div className={styles.sortIcon}>
                  {sortConfig.key === 'created' ? (
                    sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  ) : (
                    <div className={styles.sortIconPlaceholder} />
                  )}
                </div>
              </div>
              <div 
                className={`${styles.tableCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('access')}
              >
                Access
                <div className={styles.sortIcon}>
                  {sortConfig.key === 'access' ? (
                    sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  ) : (
                    <div className={styles.sortIconPlaceholder} />
                  )}
                </div>
              </div>
              <div 
                className={`${styles.tableCell} ${styles.sortableHeader}`}
                onClick={() => handleSort('role')}
              >
                Role
                <div className={styles.sortIcon}>
                  {sortConfig.key === 'role' ? (
                    sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                  ) : (
                    <div className={styles.sortIconPlaceholder} />
                  )}
                </div>
              </div>
            </div>
            
            {getSortedProjects().map((project) => (
              <div
                key={project.project.id}
                className={`${styles.projectRow} ${selectedProject?.project.id === project.project.id ? styles.selected : ''}`}
                onClick={() => handleProjectSelect(project)}
              >
                <div className={styles.tableCell} data-label="Name">
                  <span className={styles.projectName}>{project.project.name}</span>
                </div>
                <div className={styles.tableCell} data-label="Status">
                  <span className={styles.detailValue}>
                    <span className={styles.statusDot}></span>
                    {project.project.status || 'Active'}
                  </span>
                </div>
                <div className={styles.tableCell} data-label="Created">
                  <span className={styles.detailValue}>
                    {new Date(project.project.created_at || project.project.created_date).toLocaleDateString()}
                  </span>
                </div>
                <div className={styles.tableCell} data-label="Access">
                  <span className={styles.detailValue}>
                    {getSharedViaText(project)}
                  </span>
                </div>
                <div className={styles.tableCell} data-label="Role">
                  <span 
                    className={styles.accessLevel}
                    style={{ color: getAccessLevelColor(project.access_level) }}
                  >
                    {project.access_level}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Project Details */}
        {selectedProject && (
          <div className={styles.projectDetails}>
            <div className={styles.sectionHeader}>
              <h2>{selectedProject.project.name}</h2>
              <span className={styles.projectId}>ID: {selectedProject.project.id}</span>
            </div>

            <div className={styles.detailsSection}>
              <h3>Project Information</h3>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Access Level</label>
                <div className={styles.fieldValue}>
                  <span 
                    className={styles.accessBadge}
                    style={{ backgroundColor: getAccessLevelColor(selectedProject.access_level) + '20' }}
                  >
                    {selectedProject.access_level}
                  </span>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Status</label>
                <div className={styles.fieldValue}>
                  <span className={styles.statusValue}>
                    <span className={styles.statusDot}></span>
                    {selectedProject.project.status || 'Active'}
                  </span>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Created</label>
                <div className={styles.fieldValue}>
                  <span className={styles.dateValue}>
                    {new Date(selectedProject.project.created_at || selectedProject.project.created_date).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Access Method</label>
                <div className={styles.fieldValue}>
                  <span className={styles.accessMethod}>
                    {getSharedViaText(selectedProject)}
                  </span>
                </div>
              </div>
            </div>

            {/* Sharing & Permissions Section */}
            <div className={styles.detailsSection}>
              <h3>Sharing & Permissions</h3>
              
              {sharesLoading ? (
                <div className={styles.loadingShares}>
                  <div className={styles.loadingSpinner}></div>
                  <span>Loading sharing information...</span>
                </div>
              ) : (
                <>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Shared with Users</label>
                    <div className={styles.fieldValue}>
                      {shares.users && shares.users.length > 0 ? (
                        <div className={styles.sharesList}>
                          {shares.users.map((share, index) => (
                            <div key={index} className={styles.shareItem}>
                              <span className={styles.shareTarget}>{share.user_email || share.user_id}</span>
                              <span className={styles.sharePermission}>{share.permission}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className={styles.noShares}>No users have access to this project</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Shared with Teams</label>
                    <div className={styles.fieldValue}>
                      {shares.teams && shares.teams.length > 0 ? (
                        <div className={styles.sharesList}>
                          {shares.teams.map((share, index) => (
                            <div key={index} className={styles.shareItem}>
                              <span className={styles.shareTarget}>{share.team_name || share.team_id}</span>
                              <span className={styles.sharePermission}>{share.permission}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className={styles.noShares}>No teams have access to this project</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
